import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { workshopDashboardService, JobCardStatusCount } from '@/app/services/dashboardService';
import { JobCardStatus } from '@/app/types/jobCard';
import {
  Car,
  Search,
  FileText,
  ThumbsUp,
  Wrench,
  CheckCircle2,
  PackageCheck,
  Truck
} from 'lucide-react';

// Status configuration - simplified for professional appearance
const statusConfig: Record<
  JobCardStatus,
  {
    label: string;
    icon: React.ElementType;
  }
> = {
  arrival: { label: 'Arrival', icon: Car },
  inspection: { label: 'Inspection', icon: Search },
  quoted: { label: 'Quoted', icon: FileText },
  approved: { label: 'Approved', icon: ThumbsUp },
  under_service: { label: 'Under Service', icon: Wrench },
  quality_check: { label: 'Quality Check', icon: CheckCircle2 },
  ready: { label: 'Ready', icon: PackageCheck },
  delivered: { label: 'Delivered', icon: Truck },
  invoiced: { label: 'Invoiced', icon: FileText },
  cancelled: { label: 'Cancelled', icon: FileText },
};

// Statuses to display in the grid (excluding terminal ones)
const displayStatuses: JobCardStatus[] = [
  'arrival',
  'inspection',
  'quoted',
  'approved',
  'under_service',
  'quality_check',
  'ready',
  'delivered',
];

interface StatusCardProps {
  status: JobCardStatus;
  count: number;
  onClick: () => void;
}

const StatusCard: React.FC<StatusCardProps> = ({ status, count, onClick }) => {
  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <div
      className="p-[1px] rounded-xl bg-gradient-to-br from-slate-200 via-slate-100 to-slate-200 dark:from-slate-700 dark:via-slate-800 dark:to-slate-700 cursor-pointer transition-all duration-200 hover:shadow-md hover:from-slate-300 hover:via-slate-200 hover:to-slate-300"
      onClick={onClick}
    >
      <div className="bg-white dark:bg-card rounded-[11px] p-4 h-full">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-normal text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              {config.label}
            </p>
            <p className="text-2xl font-medium text-slate-800 dark:text-slate-200 mt-1">{count}</p>
          </div>
          <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-slate-50 dark:bg-slate-800">
            <Icon className="h-5 w-5 text-slate-500 dark:text-slate-400" />
          </div>
        </div>
      </div>
    </div>
  );
};

const StatusCardSkeleton: React.FC = () => (
  <div className="p-[1px] rounded-xl bg-gradient-to-br from-slate-200 via-slate-100 to-slate-200 dark:from-slate-700 dark:via-slate-800 dark:to-slate-700">
    <div className="bg-white dark:bg-card rounded-[11px] p-4">
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-3 w-20 mb-2" />
          <Skeleton className="h-8 w-12" />
        </div>
        <Skeleton className="h-10 w-10 rounded-lg" />
      </div>
    </div>
  </div>
);

const JobCardStatusGrid: React.FC = () => {
  const navigate = useNavigate();

  const { data: statusCounts, isLoading } = useQuery({
    queryKey: ['workshopDashboard', 'statusCounts'],
    queryFn: () => workshopDashboardService.getJobCardStatusCounts(),
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 15000,
  });

  const getCountForStatus = (status: JobCardStatus): number => {
    if (!statusCounts) return 0;
    const found = statusCounts.find((s: JobCardStatusCount) => s.status === status);
    return found?.count || 0;
  };

  const handleCardClick = (status: JobCardStatus) => {
    // The list page expects filters as JSON-encoded query param
    const filters = encodeURIComponent(JSON.stringify({ status }));
    navigate(`/workshop/job-cards/list?filters=${filters}`);
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {displayStatuses.map((status) => (
          <StatusCardSkeleton key={status} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {displayStatuses.map((status) => (
        <StatusCard
          key={status}
          status={status}
          count={getCountForStatus(status)}
          onClick={() => handleCardClick(status)}
        />
      ))}
    </div>
  );
};

export default JobCardStatusGrid;
